import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { NOTIFICATION_TYPE } from '../../../../constants/common'
import { ROUTE_PATH_LIST_AUTH } from '../../../../constants/route'
import { useInlineNotifications } from '../../../../hooks'
import { FormikForm } from '../../../Formik'
import { recoverPassword } from '../../Auth.api'
import { hasNotifications } from '../../Auth.utils'
import { AuthFooter } from '../../AuthFooter'
import { AuthFormWrapper } from '../../AuthFormWrapper'
import { AuthHeader } from '../../AuthHeader'
import { AuthHelpText } from '../../AuthHelpText'
import { AuthLicense } from '../../AuthLicense'
import { AuthPanel } from '../../AuthPanel'
import { AuthTitle } from '../../AuthTitle'

import {
  RECOVER_PASSWORD_FORM_FIELD_NAME_EMAIL,
  RECOVER_PASSWORD_FORM_INITIAL_VALUES,
  RECOVER_PASSWORD_FORM_NOTIFICATION_ERROR_ID,
} from './RecoverPasswordForm.constants'
import {
  getRecoverPasswordFormValidationSchema,
  parseRecoverPasswordFormError,
} from './RecoverPasswordForm.utils'
import { RecoverPasswordFormFields } from './RecoverPasswordFormFields'

import * as styles from './RecoverPasswordForm.module.css'

function RecoverPasswordForm({
  getValidationSchema = getRecoverPasswordFormValidationSchema,
  initialValues = RECOVER_PASSWORD_FORM_INITIAL_VALUES,
}) {
  const { t } = useTranslation()

  const [successActionEmail, setSuccessActionEmail] = useState(null)

  const { handleAddNotification, handleRemoveNotification, notifications } =
    useInlineNotifications()

  const validationSchema = getValidationSchema()

  function handleSubmit(data, { setSubmitting }) {
    handleRemoveNotification(RECOVER_PASSWORD_FORM_NOTIFICATION_ERROR_ID)

    recoverPassword(data)
      .then(() => {
        setSuccessActionEmail(data[RECOVER_PASSWORD_FORM_FIELD_NAME_EMAIL])
      })
      .catch(error => {
        handleAddNotification({
          id: RECOVER_PASSWORD_FORM_NOTIFICATION_ERROR_ID,
          text: parseRecoverPasswordFormError(error),
          type: NOTIFICATION_TYPE.error,
        })
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const withNotifications = hasNotifications(notifications)

  const titleClassName = classNames(styles.title, {
    [styles.withNotifications]: withNotifications,
  })

  return (
    <AuthPanel withNotifications={withNotifications}>
      <AuthHeader
        description={t(
          successActionEmail
            ? 'Auth.SignInDescriptionKey'
            : 'Auth.SignUpDescriptionKey'
        )}
        link={
          successActionEmail
            ? {
                text: t('Auth.SignInLinkKey'),
                to: ROUTE_PATH_LIST_AUTH.login(),
              }
            : {
                text: t('Auth.SignUpLinkKey'),
                to: ROUTE_PATH_LIST_AUTH.registration,
              }
        }
        notifications={notifications}
      />

      <AuthFormWrapper>
        <AuthTitle
          className={titleClassName}
          title={t(
            successActionEmail
              ? 'Auth.RecoverPasswordCompletedTitleKey'
              : 'Auth.RecoverPasswordTitleKey'
          )}
        />
        <AuthHelpText
          className={styles.helpText}
          text={
            successActionEmail ? (
              <Trans
                components={{ strong: <strong /> }}
                i18nKey='Auth.RecoverPasswordCompletedTextKey'
                values={{ email: successActionEmail }}
              />
            ) : (
              t('Auth.RecoverPasswordTextKey')
            )
          }
        />
        {successActionEmail ? null : (
          <FormikForm
            initialValues={initialValues}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            <RecoverPasswordFormFields
              onRemoveNotification={handleRemoveNotification}
              onSetNotification={handleAddNotification}
            />
          </FormikForm>
        )}
        {successActionEmail ? null : <AuthLicense className={styles.license} />}
      </AuthFormWrapper>

      <AuthFooter
        showLicense={!successActionEmail}
        withNotifications={withNotifications}
      />
    </AuthPanel>
  )
}

export default RecoverPasswordForm
